/**
 * Telemedicine · Phase 1 · P1.UI Phase E — Lobby
 *
 * Pre-call lobby. Mounts after the Permissions screen and before the in-call
 * screen. Two responsibilities:
 *
 *   1. Mint the RTC token up front so the user gets a live local camera
 *      preview while they verify their setup. The creds are then handed off
 *      to /video-call/[channel] via the in-memory handoff store
 *      (services/videoCallHandoff.ts) so we don't double-mint.
 *
 *   2. Let the user toggle their mic + camera + flip cameras BEFORE joining.
 *      Whatever state they leave the lobby in is what the in-call screen
 *      starts the call with (publishMicrophoneTrack/publishCameraTrack are
 *      flipped accordingly inside useAgoraCall.join).
 *
 * Visual: matches Figma nodes 6688:20259 (lobby on) and 6688:20429 (lobby off).
 * Composed from reusable bits under components/video/lobby/* so Phase B/C/D
 * (Fatal, At-the-Meet, Waiting) can reuse the chrome.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAgoraCall } from '@/hooks/useAgoraCall';
import {
  peekLinkTokenPayload,
  requestVideoToken,
  VideoTokenError,
} from '@/services/videoService';
import { useAuth } from '@/contexts/AuthContext';
import { setHandoff } from '@/services/videoCallHandoff';
import LobbyHeader from '@/components/video/lobby/LobbyHeader';
import LobbyControlPill, { RecordVideoGlyph } from '@/components/video/lobby/LobbyControlPill';
import LobbyPreviewSurface from '@/components/video/lobby/LobbyPreviewSurface';
import LobbyFatalScreen from '@/components/video/lobby/LobbyFatalScreen';
import { resolveAvatarUrl } from '@/services/profilePhotoCacheService';

const CREAM = '#F9F8F3';
const INK = '#151515';
const CARARRA = '#F0EEE4';

export default function VideoCallLobbyScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{
    sessionType?: string;
    sessionId?: string;
    mode?: 'audio' | 'video';
    linkToken?: string;
  }>();

  // AuthContext exposes the profile as `userProfile` — destructure it under
  // that name and alias to `profile` locally so the rest of this file
  // (firstName / avatarUrl memos) keeps reading from the right object.
  // Without the alias, `profile?.avatar_url` is always undefined, the cache
  // fallback is the only path that runs, and cold launches show the
  // generic Avatar glyph instead of the user's real photo.
  const { session, user, userProfile: profile } = useAuth() as any;
  const call = useAgoraCall();

  const sessionType = String(params.sessionType ?? 'appointment');
  const wantsVideo = (params.mode ?? 'video') === 'video';
  const linkToken = params.linkToken ? String(params.linkToken) : undefined;

  // Deep-link surfaces only carry channel + token, so peek at the JWT's `sid`
  // claim when the explicit param is missing.
  const sessionId = useMemo(() => {
    const fromParams = String(params.sessionId ?? '');
    if (fromParams) return fromParams;
    if (linkToken) {
      const payload = peekLinkTokenPayload(linkToken);
      if (payload?.sid) return String(payload.sid);
    }
    return '';
  }, [params.sessionId, linkToken]);

  const [creds, setCreds] = useState<Awaited<ReturnType<typeof requestVideoToken>> | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  // The hook owns the engine + actual mic/camera state. Reading from it keeps
  // lobby state and engine state perfectly aligned even mid-toggle.
  const fetchedRef = useRef(false);
  const enteringRef = useRef(false);
  const micOn = call.micEnabled;
  const camOn = call.cameraEnabled && wantsVideo;

  // Mint the RTC token + start preview on mount (once).
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    if (!sessionId) {
      setFatal('Sessão inválida.');
      return;
    }
    (async () => {
      try {
        const next = await requestVideoToken({
          sessionType,
          sessionId,
          linkToken,
          accessToken: session?.access_token,
        });
        setCreds(next);
        // Open the engine and start the local preview. Doesn't join anything.
        call.previewLocal({ appId: next.appId, video: wantsVideo });
      } catch (err) {
        console.error('❌ [video-call/lobby] token mint failed', {
          sessionType,
          sessionId,
          hasLinkToken: !!linkToken,
          hasAccessToken: !!session?.access_token,
          errorName: err instanceof Error ? err.name : typeof err,
          errorMessage: err instanceof Error ? err.message : String(err),
          videoTokenErrorCode: err instanceof VideoTokenError ? err.code : undefined,
        });
        const msg =
          err instanceof VideoTokenError
            ? mapTokenError(err)
            : 'Não foi possível preparar a sala.';
        setFatal(msg);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleMic = useCallback(() => {
    if (!creds) return;
    call.toggleMic();
  }, [call, creds]);

  const handleToggleCam = useCallback(() => {
    if (!creds || !wantsVideo) return;
    call.toggleCamera();
  }, [call, creds, wantsVideo]);

  const handleFlip = useCallback(() => {
    if (!camOn) return;
    call.switchCamera();
  }, [call, camOn]);

  const handleEnter = useCallback(() => {
    if (!creds) return;
    // Synchronous double-tap guard: a second tap within the same tick would
    // re-write the handoff entry AND fire a second router.replace.
    if (enteringRef.current) return;
    enteringRef.current = true;
    setHandoff(sessionId, {
      creds,
      mode: wantsVideo ? 'video' : 'audio',
      initialMicMuted: !micOn,
      initialCamOff: !camOn,
    });
    router.replace({
      pathname: '/video-call/[channel]',
      params: {
        channel: String(creds.channel ?? sessionId),
        sessionType,
        sessionId,
        mode: wantsVideo ? 'video' : 'audio',
      },
    });
  }, [creds, sessionId, micOn, camOn, sessionType, wantsVideo, router]);

  const exit = useCallback(() => {
    if (router.canGoBack()) router.back();
    else router.replace('/diary');
  }, [router]);

  // Fatal screen routes the CTA to home rather than back-stack so the user
  // doesn't bounce back to whatever screen pushed the lobby (which could be
  // the deep-link "loading" route on a cold launch — there's nothing useful
  // to land on, and on a normal flow the back-stack lands on perfil which
  // is exactly the screen the user is trying to escape from).
  // NOTE: the home tab is `app/(tabs)/index.tsx` — `/(tabs)/home` does NOT
  // resolve and silently falls back to the last-active tab.
  const exitToHome = useCallback(() => {
    router.replace('/diary');
  }, [router]);

  const firstName = useMemo(() => {
    const full = (profile?.full_name || user?.user_metadata?.full_name || '').trim();
    if (!full) return '';
    return full.split(/\s+/)[0];
  }, [profile?.full_name, user?.user_metadata?.full_name]);

  // Profiles store either a fully-qualified URL (Google avatars, etc.) or a
  // relative Supabase storage path. `resolveAvatarUrl` normalises both into
  // a URL the <Image> can actually load — without it, relative paths fall
  // through to the beige fallback even when the user has uploaded a photo.
  const avatarUrl = useMemo(
    () =>
      resolveAvatarUrl(
        profile?.avatar_url ||
          user?.user_metadata?.avatar_url ||
          user?.user_metadata?.picture ||
          null,
      ),
    [profile?.avatar_url, user?.user_metadata?.avatar_url, user?.user_metadata?.picture],
  );

  const titleText = firstName
    ? `${firstName}, prepare-se para a sua sessão antes de entrar`
    : 'Prepare-se para a sua sessão antes de entrar';

  if (fatal) {
    // The Figma copy is intentionally generic. We pass the mapped token-error
    // string as the body so the user still gets situational nuance
    // ("link expirou", "compromisso não encontrado", etc.) without burying
    // the headline.
    return <LobbyFatalScreen body={fatal} onExit={exitToHome} />;
  }

  const ready = !!creds;

  return (
    <SafeAreaView style={styles.safe}>
      <LobbyHeader title="Agendamento" onClose={exit} />

      <View style={styles.body}>
        {/* Camera badge + serif headline */}
        <View style={styles.headlineBlock}>
          <View style={styles.cameraBadge}>
            {/* Figma 6688:20549 — body filled white, speaker bump filled ink. */}
            <RecordVideoGlyph color={INK} size={20} filled />
          </View>
          <Text style={styles.title}>{titleText}</Text>
        </View>

        {/* Local preview */}
        <LobbyPreviewSurface
          cameraOn={camOn}
          ready={ready}
          firstName={firstName}
          avatarUrl={avatarUrl}
          user={user ? { id: user.id } : null}
        />

        {/* Toggle pills row */}
        <View style={styles.pillsRow}>
          <LobbyControlPill
            variant="mic"
            on={micOn}
            label="Mic"
            onPress={handleToggleMic}
            disabled={!ready}
            audioLevel={call.localAudioLevel}
          />
          <LobbyControlPill
            variant="camera"
            on={camOn}
            label="Câmera"
            onPress={handleToggleCam}
            disabled={!ready || !wantsVideo}
          />
          <LobbyControlPill
            variant="flip"
            label="Girar"
            onPress={handleFlip}
            disabled={!ready || !camOn}
          />
        </View>
      </View>

      {/* Primary CTA */}
      <View style={styles.footer}>
        <TouchableOpacity
          style={[styles.primaryButton, !ready && styles.primaryButtonDisabled]}
          onPress={handleEnter}
          disabled={!ready}
          activeOpacity={0.85}
          accessibilityRole="button"
        >
          {!ready ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.primaryButtonText}>Entrar na sessão</Text>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

function mapTokenError(err: VideoTokenError): string {
  switch (err.code) {
    case 'not_authenticated':
      return 'Você precisa estar conectado para entrar na chamada.';
    case 'patient_link_expired':
    case 'sponsor_link_expired':
      return 'Este link de chamada expirou. Abra o convite mais recente do seu e-mail.';
    case 'invalid_patient_link':
    case 'invalid_sponsor_link':
      return 'Link de chamada inválido.';
    case 'CANCELED':
      return 'Este compromisso foi cancelado.';
    case 'WINDOW_CLOSED':
      return 'Esta chamada ainda não está disponível ou já foi encerrada.';
    case 'NOT_FOUND':
      return 'Compromisso não encontrado.';
    case 'FORBIDDEN':
      return 'Você não tem permissão para entrar nesta chamada.';
    case 'agora_not_configured':
      return 'Serviço de vídeo indisponível. Tente novamente mais tarde.';
    default:
      return 'Não foi possível preparar a sala.';
  }
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: CREAM },
  body: {
    flex: 1,
    padding: 24,
    gap: 16,
    alignItems: 'center',
  },
  headlineBlock: {
    alignItems: 'center',
    gap: 16,
    width: '100%',
  },
  cameraBadge: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: CARARRA,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    color: INK,
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    textAlign: 'center',
  },
  pillsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    width: '100%',
  },
  footer: {
    padding: 24,
  },
  primaryButton: {
    width: '100%',
    height: 56,
    borderRadius: 12,
    backgroundColor: INK,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonDisabled: { opacity: 0.6 },
  primaryButtonText: {
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
  },
});
